<script setup>
import { ref, inject } from 'vue';
import { AxiosAPI } from '@/axios';
import { useMainStore } from '../../../stores/index';
import ModalOverlay from './ModalOverlay.vue';

const Store = useMainStore();
const emitter = inject('emitter');

const viewDocument = ref(null);
const id = ref(0);
const taskName = ref('');
const isSubmitting = ref(false);
const deleteOption = ref('single');

const openModalDelete = (taskId, name) => {
  id.value = taskId;
  taskName.value = name || '';
  deleteOption.value = 'single';
  if (viewDocument.value) viewDocument.value.openModal();
};

const deleteTask = async (endpoint) => {
  if (isSubmitting.value) return;

  isSubmitting.value = true;

  try {
    await AxiosAPI.delete(`/api/tasks/${id.value}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${Store.accessToken}`
      }
    });

    if (emitter) {
      emitter.emit('KanbanUpdated');
    }
    if (viewDocument.value) viewDocument.value.closeModal();

    id.value = 0;
    taskName.value = '';
    deleteOption.value = 'single';
  } catch (error) {
    console.error('Erro ao excluir tarefa:', error);
  } finally {
    isSubmitting.value = false;
  }
};

const handleDeleteClick = async () => {
  const endpoints = {
    single: '/',
    future: '/future',
    all: '/all'
  };

  const endpoint = endpoints[deleteOption.value] || '/';
  await deleteTask(endpoint);
};

defineExpose({
  openModalDelete
});
</script>

<style scoped>
.warning-icon {
  color: #F1860B;
}

.text-muted {
  color: #6c757d;
  font-size: 14px;
}

.radio-option {
  padding: 12px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.radio-option:hover {
  background-color: #f8f9fa;
}

.radio-option.selected {
  border-color: #dc3545;
  background-color: #fff5f5;
}

.radio-option input[type="radio"] {
  margin-right: 10px;
  cursor: pointer;
}

.radio-option label {
  cursor: pointer;
  margin: 0;
  font-weight: 400;
}
</style>

<template>
  <div>
    <ModalOverlay ref="viewDocument">
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <span class="font-docs">
              <i class="fa fa-exclamation-triangle mr-2 warning-icon"></i>
              <b>Excluir Atividade</b>
            </span>
          </div>
          <div>
            <button
              id="close"
              type="button"
              class="close"
              @click="viewDocument.closeModal()"
              :disabled="isSubmitting"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </div>
      </template>

      <template #body>
        <div>
          <p class="font-docs mb-3" v-if="taskName">
            Deseja excluir a atividade <strong>{{ taskName }}</strong>?
          </p>
          <p class="font-docs mb-3" v-else>
            Deseja excluir esta atividade?
          </p>
          <p class="font-docs text-muted mb-3">
            Escolha uma das opções abaixo:
          </p>

          <div>
            <div 
              class="radio-option" 
              :class="{ selected: deleteOption === 'single' }"
              @click="deleteOption = 'single'"
            >
              <input 
                type="radio" 
                id="option-single" 
                value="single" 
                v-model="deleteOption"
              />
              <label for="option-single" class="font-docs">
                Excluir apenas esta atividade específica
              </label>
            </div>

            <div 
              class="radio-option" 
              :class="{ selected: deleteOption === 'future' }"
              @click="deleteOption = 'future'"
            >
              <input 
                type="radio" 
                id="option-future" 
                value="future" 
                v-model="deleteOption"
              />
              <label for="option-future" class="font-docs">
                Excluir esta atividade e todas as futuras da série
              </label>
            </div>

            <div 
              class="radio-option" 
              :class="{ selected: deleteOption === 'all' }"
              @click="deleteOption = 'all'"
            >
              <input 
                type="radio" 
                id="option-all" 
                value="all" 
                v-model="deleteOption"
              />
              <label for="option-all" class="font-docs">
                Excluir todas as atividades da série (passadas e futuras)
              </label>
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="d-flex justify-content-end mt-4">
          <button 
            class="btn btn-secondary font-docs mr-3" 
            @click="viewDocument.closeModal()"
            :disabled="isSubmitting"
          >
            Cancelar
          </button>
          <button 
            class="btn btn-danger font-docs text-white" 
            @click="handleDeleteClick"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? 'Excluindo...' : 'Excluir' }}
          </button>
        </div>
      </template>
    </ModalOverlay>
  </div>
</template>